import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom'
import {
  getCliente,
  createCliente,
  updateCliente,
  deleteCliente,
  getAgendamentosDoCliente,
} from '../../lib/supabase'
import { useTenant } from '../../contexts/TenantContext'
import { useToast } from '../../contexts/ToastContext'
import { estadoCfg } from '../../lib/estados'
import Loading from '../../components/Loading'

const inputStyle = {
  width: '100%', padding: '9px 12px',
  border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
  fontSize: '14px', fontFamily: 'var(--font-ui)',
  background: 'var(--cor-branco)', color: 'var(--cor-texto)',
}

function formatarDataHora(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })
}

function BadgeEstado({ estado }) {
  const cfg = estadoCfg(estado)
  return (
    <span style={{
      background: cfg.cor + '18', color: cfg.cor,
      padding: '2px 8px', borderRadius: '999px',
      fontSize: '11px', fontWeight: '500',
    }}>
      {cfg.label}
    </span>
  )
}

function PiscoAtivo({ ativo, onChange, guardando }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      cursor: guardando ? 'default' : 'pointer', userSelect: 'none',
    }}>
      <div
        onClick={guardando ? undefined : onChange}
        style={{
          width: '40px', height: '22px', borderRadius: '999px',
          background: ativo ? 'var(--cor-principal)' : '#D1D5DB',
          position: 'relative', transition: 'background 0.2s',
          opacity: guardando ? 0.6 : 1, flexShrink: 0,
        }}
      >
        <div style={{
          position: 'absolute', top: '3px',
          left: ativo ? '21px' : '3px',
          width: '16px', height: '16px', borderRadius: '50%',
          background: 'white', transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
      <span style={{ fontSize: '13px', fontWeight: '500', color: ativo ? 'var(--cor-principal)' : 'var(--cor-texto-suave)' }}>
        {ativo ? 'Cliente ativo' : 'Cliente inativo'}
      </span>
    </label>
  )
}

export default function DetalheCliente() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { tenant } = useTenant()

  // Volta à lista preservando os filtros/página (que ficam nos query params do
  // URL de origem). Se não houver histórico interno — ex. abriu o detalhe por
  // link direto — cai de volta para a lista sem filtros.
  function voltarParaLista() {
    if (location.key !== 'default') navigate(-1)
    else navigate('/admin/clientes')
  }
  const toast = useToast()
  const isNovo = id === 'novo'

  const [cliente, setCliente] = useState(null)
  const [carregando, setCarregando] = useState(!isNovo)
  const [guardando, setGuardando] = useState(false)
  const [guardandoAtivo, setGuardandoAtivo] = useState(false)
  const [confirmarEliminar, setConfirmarEliminar] = useState(false)
  const [eliminando, setEliminando] = useState(false)

  const [agendamentos, setAgendamentos] = useState([])
  const [carregandoHistorico, setCarregandoHistorico] = useState(false)

  const [form, setForm] = useState({
    nome: '',
    telefone: '',
    nome_amigavel: '',
    email: '',
    nif: '',
    sexo: '',
    data_nascimento: '',
    morada: '',
    codigo_postal: '',
    observacoes: '',
    aceita_promocoes: false,
  })

  useEffect(() => {
    if (!tenant || isNovo) return
    getCliente(tenant.id, id)
      .then(c => {
        if (!c) { navigate('/admin/clientes', { replace: true }); return }
        setCliente(c)
        setForm({
          nome: c.nome || '',
          telefone: c.telefone || '',
          nome_amigavel: c.nome_amigavel || '',
          email: c.email || '',
          nif: c.nif || '',
          sexo: c.sexo || '',
          data_nascimento: c.data_nascimento || '',
          morada: c.morada || '',
          codigo_postal: c.codigo_postal || '',
          observacoes: c.observacoes || '',
          aceita_promocoes: c.aceita_promocoes || false,
        })
      })
      .catch(e => toast.erro(e.message))
      .finally(() => setCarregando(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, id])

  useEffect(() => {
    if (!tenant || isNovo || !id) return
    setCarregandoHistorico(true)
    getAgendamentosDoCliente(tenant.id, id)
      .then(setAgendamentos)
      .catch(e => toast.erro(e.message))
      .finally(() => setCarregandoHistorico(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, id])

  async function handleSubmit(e) {
    e.preventDefault()
    setGuardando(true)
    try {
      if (isNovo) {
        const novoId = await createCliente(tenant.id, {
          nome: form.nome.trim(),
          telefone: form.telefone.trim(),
          nome_amigavel: form.nome_amigavel.trim() || null,
          email: form.email.trim() || null,
          nif: form.nif.trim() || null,
          sexo: form.sexo || null,
          data_nascimento: form.data_nascimento || null,
          morada: form.morada.trim() || null,
          codigo_postal: form.codigo_postal.trim() || null,
          observacoes: form.observacoes.trim() || null,
          aceita_promocoes: !!form.aceita_promocoes,
        })
        toast.sucesso('Cliente criado.')
        navigate(`/admin/clientes/${novoId}`, { replace: true })
      } else {
        await updateCliente(tenant.id, id, {
          nome_amigavel: form.nome_amigavel.trim() || null,
          email: form.email.trim() || null,
          nif: form.nif.trim() || null,
          sexo: form.sexo || null,
          data_nascimento: form.data_nascimento || null,
          morada: form.morada.trim() || null,
          codigo_postal: form.codigo_postal.trim() || null,
          observacoes: form.observacoes.trim() || null,
          aceita_promocoes: !!form.aceita_promocoes,
        })
        toast.sucesso('Cliente atualizado.')
      }
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setGuardando(false)
    }
  }

  async function eliminar() {
    setEliminando(true)
    try {
      const r = await deleteCliente(tenant.id, id)
      const n = r?.agendamentos_apagados || 0
      toast.sucesso(n > 0
        ? `Cliente e ${n} marcação(ões) eliminados.`
        : 'Cliente eliminado.')
      navigate('/admin/clientes', { replace: true })
    } catch (e) {
      toast.erro(e.message)
      setEliminando(false)
      setConfirmarEliminar(false)
    }
  }

  async function toggleAtivo() {
    if (!cliente) return
    setGuardandoAtivo(true)
    const novoAtivo = !cliente.ativo
    try {
      await updateCliente(tenant.id, id, { ativo: novoAtivo })
      setCliente(prev => ({ ...prev, ativo: novoAtivo }))
      toast.sucesso(novoAtivo ? 'Cliente reativado.' : 'Cliente desativado.')
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setGuardandoAtivo(false)
    }
  }

  if (carregando) return <div style={{ padding: '64px 24px' }}><Loading /></div>

  const nomeDisplay = cliente ? (cliente.nome_amigavel || cliente.nome) : 'Novo Cliente'
  const totalMarcacoes = agendamentos.length
  const concluidosMarcacoes = agendamentos.filter(a => a.estado === 'concluido').length

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '32px 24px 64px' }}>

      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px', flexWrap: 'wrap' }}>
        <button
          onClick={voltarParaLista}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cor-texto-suave)', fontSize: '20px', lineHeight: 1, padding: '4px' }}
          aria-label="Voltar à lista"
        >
          ←
        </button>
        {!isNovo && (
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%',
            background: cliente?.ativo ? 'var(--cor-principal)' : 'var(--cor-texto-suave)',
            color: 'white', fontSize: '16px', fontWeight: '700',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {nomeDisplay.charAt(0).toUpperCase()}
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '20px', margin: 0 }}>{nomeDisplay}</h2>
            {!isNovo && !cliente?.ativo && (
              <span style={{ fontSize: '10px', background: '#F3F4F6', color: '#6B7280', padding: '2px 8px', borderRadius: '999px', fontWeight: '500' }}>
                Inativo
              </span>
            )}
          </div>
          {!isNovo && cliente?.nome && cliente.nome_amigavel && cliente.nome_amigavel !== cliente.nome && (
            <div style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', marginTop: '2px' }}>{cliente.nome}</div>
          )}
        </div>

        {/* Pisco ativo no cabeçalho */}
        {!isNovo && cliente && (
          <PiscoAtivo ativo={cliente.ativo} onChange={toggleAtivo} guardando={guardandoAtivo} />
        )}
      </div>

      {/* Formulário */}
      <section style={{
        background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)',
        borderRadius: 'var(--raio-lg)', padding: '24px',
        boxShadow: 'var(--sombra)', marginBottom: '24px',
      }}>
        <p style={{ fontWeight: '600', fontSize: '14px', color: 'var(--cor-principal)', marginBottom: '20px' }}>
          {isNovo ? 'Dados do cliente' : 'Editar dados'}
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>

            {isNovo && (
              <>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                    Nome <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={form.nome}
                    onChange={e => setForm(p => ({ ...p, nome: e.target.value }))}
                    placeholder="Nome completo"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                    Telefone <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={form.telefone}
                    onChange={e => setForm(p => ({ ...p, telefone: e.target.value }))}
                    placeholder="+351 912 345 678"
                    style={inputStyle}
                  />
                </div>
              </>
            )}

            <div style={{ gridColumn: isNovo ? '1 / -1' : undefined }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                Nome amigável
              </label>
              <input
                type="text"
                value={form.nome_amigavel}
                onChange={e => setForm(p => ({ ...p, nome_amigavel: e.target.value }))}
                placeholder={isNovo ? 'Apelido ou alcunha (opcional)' : (cliente?.nome || '')}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>NIF</label>
              <input
                type="text"
                value={form.nif}
                onChange={e => setForm(p => ({ ...p, nif: e.target.value }))}
                maxLength={9}
                placeholder="000000000"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Data de Nascimento</label>
              <input
                type="date"
                value={form.data_nascimento}
                onChange={e => setForm(p => ({ ...p, data_nascimento: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Sexo</label>
              <select
                value={form.sexo}
                onChange={e => setForm(p => ({ ...p, sexo: e.target.value }))}
                style={inputStyle}
              >
                <option value="">—</option>
                <option value="F">Feminino</option>
                <option value="M">Masculino</option>
                <option value="O">Outro</option>
              </select>
            </div>
            {!isNovo && cliente?.telefone && (
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Telefone</label>
                <div style={{ ...inputStyle, background: 'var(--cor-creme)', color: 'var(--cor-texto-suave)', cursor: 'default' }}>
                  {cliente.telefone}
                </div>
              </div>
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Morada</label>
              <input
                type="text"
                value={form.morada}
                onChange={e => setForm(p => ({ ...p, morada: e.target.value }))}
                placeholder="Rua, número, andar…"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Código Postal</label>
              <input
                type="text"
                value={form.codigo_postal}
                onChange={e => setForm(p => ({ ...p, codigo_postal: e.target.value }))}
                placeholder="0000-000"
                maxLength={8}
                style={inputStyle}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Observações</label>
              <textarea
                value={form.observacoes}
                onChange={e => setForm(p => ({ ...p, observacoes: e.target.value }))}
                rows={3}
                maxLength={1000}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Notas internas sobre o cliente…"
              />
            </div>
            <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.aceita_promocoes}
                onChange={e => setForm(p => ({ ...p, aceita_promocoes: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: 'var(--cor-principal)', flexShrink: 0 }}
              />
              <span style={{ fontSize: '13px', color: 'var(--cor-texto)' }}>
                Aceita receber promoções
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="submit" className="btn-primario" disabled={guardando} style={{ opacity: guardando ? 0.6 : 1, padding: '9px 22px', fontSize: '14px' }}>
              {guardando ? 'A guardar…' : (isNovo ? 'Criar Cliente' : 'Guardar')}
            </button>
            <button type="button" onClick={voltarParaLista} className="btn-secundario" style={{ padding: '9px 22px', fontSize: '14px' }}>
              Cancelar
            </button>
            {!isNovo && !carregandoHistorico && (
              confirmarEliminar ? (
                <>
                  <span style={{ fontSize: '13px', color: '#DC2626', fontWeight: '500' }}>
                    {agendamentos.length > 0
                      ? `Isto apaga o cliente e ${agendamentos.length} marcação(ões) do histórico. Irreversível. Tens a certeza?`
                      : 'Tens a certeza?'}
                  </span>
                  <button
                    type="button"
                    onClick={eliminar}
                    disabled={eliminando}
                    style={{ background: '#DC2626', border: 'none', borderRadius: 'var(--raio)', padding: '9px 16px', fontSize: '13px', color: 'white', cursor: eliminando ? 'default' : 'pointer', opacity: eliminando ? 0.6 : 1 }}
                  >
                    {eliminando ? 'A eliminar…' : 'Confirmar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmarEliminar(false)}
                    disabled={eliminando}
                    style={{ background: 'none', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '9px 14px', fontSize: '13px', color: 'var(--cor-texto-suave)', cursor: 'pointer' }}
                  >
                    Cancelar
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmarEliminar(true)}
                  style={{ marginLeft: 'auto', background: 'none', border: '1px solid #FCA5A5', borderRadius: 'var(--raio)', padding: '9px 16px', fontSize: '13px', color: '#DC2626', cursor: 'pointer' }}
                >
                  Eliminar cliente
                </button>
              )
            )}
          </div>
        </form>
      </section>

      {/* Histórico — só para clientes existentes */}
      {!isNovo && (
        <section style={{
          background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)',
          borderRadius: 'var(--raio-lg)', padding: '24px',
          boxShadow: 'var(--sombra)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <p style={{ fontWeight: '600', fontSize: '14px', color: 'var(--cor-principal)', margin: 0 }}>
              Histórico de Marcações
            </p>
            <Link
              to={`/admin/clientes/${id}/cobrar`}
              style={{ fontSize: '13px', textDecoration: 'none', color: 'var(--cor-principal)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '6px 12px', whiteSpace: 'nowrap' }}
            >
              🧾 Cobrar em conjunto
            </Link>
          </div>

          {carregandoHistorico && <Loading />}

          {!carregandoHistorico && (
            <>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ background: 'var(--cor-creme)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '10px 20px', textAlign: 'center', minWidth: '80px' }}>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--cor-principal)' }}>{totalMarcacoes}</div>
                  <div style={{ fontSize: '11px', color: 'var(--cor-texto-suave)' }}>Total</div>
                </div>
                <div style={{ background: 'var(--cor-creme)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', padding: '10px 20px', textAlign: 'center', minWidth: '80px' }}>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#166534' }}>{concluidosMarcacoes}</div>
                  <div style={{ fontSize: '11px', color: 'var(--cor-texto-suave)' }}>Concluídas</div>
                </div>
              </div>

              {agendamentos.length === 0 ? (
                <p style={{ color: 'var(--cor-texto-suave)', fontSize: '13px', textAlign: 'center', padding: '24px' }}>
                  Sem marcações registadas.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {agendamentos.map(ag => (
                    <button
                      key={ag.id}
                      onClick={() => navigate(`/admin/agendamento/${ag.id}`)}
                      style={{
                        background: 'var(--cor-creme)', border: '1px solid var(--cor-borda)',
                        borderRadius: 'var(--raio)', padding: '12px 14px',
                        display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
                        cursor: 'pointer', textAlign: 'left', width: '100%',
                        transition: 'border-color 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--cor-principal)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--cor-borda)'}
                    >
                      {ag.profissionais?.cor_agenda && (
                        <div style={{
                          width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                          background: ag.profissionais.cor_agenda,
                        }} />
                      )}
                      <div style={{ flex: 1, minWidth: '140px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>
                          {ag.servicos?.nome || '—'}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--cor-texto-suave)', marginTop: '2px' }}>
                          {formatarDataHora(ag.data_hora_inicio)}
                          {ag.profissionais?.nome && ` · ${ag.profissionais.nome}`}
                        </div>
                      </div>
                      <BadgeEstado estado={ag.estado} />
                      <span style={{ fontSize: '14px', color: 'var(--cor-texto-suave)', flexShrink: 0 }}>›</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      )}

    </div>
  )
}
